<template>
  <h2 class="px-1 py-2 mr-1 bg-gray-800 rounded-t-lg text-gray-100 flex flex-row">
    <div class="grow px-3 py-1">
      <span class="text-text-secondary text-sm pr-2">
        {{ startedAt }}
      </span>

      {{ activity.name }}

      {{ difficulty }}
      <span
        v-if="activity.list_type != 'open_practice'"
        class="activity-type-label text-text-primary bg-black rounded-xl px-2 py-1 ml-2 hidden lg:inline"
      >
        <img :src="activityTypeIconUrl(true)">

        {{ activityTypeName }}
      </span>
    </div>

    <button
      v-if="activity.list_type == 'open_practice'"
      :class="getPlayButtonClass()"
      @click="$router.push('/play')">
      {{ $t('training.activity.play_now') }}
    </button>

    <button
      v-else-if="activity.list_type == 'coach'"
      :class="getPlayButtonClass()"
      @click="$router.push('/coach')">
      {{ $t('training.activity.coach') }}
    </button>

    <Menu as="div" v-else class="relative">
      <MenuButton :class="getPlayButtonClass()">
        {{ $t('training.activity.play') }}
        <ChevronDownIcon class="h-4 w-4 -mr-1" aria-hidden="true" />
      </MenuButton>
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <MenuItems class="absolute right-0 z-50 mt-2 rounded-md bg-gray-900 border border-gray-900 shadow-lg overflow-hidden">
          <!-- Routine -->
          <MenuItem as="div" v-if="activity.list_type == 'routine'">
            <button :class="getMenuItemButtonClass()" @click="$router.push(`/routines/${activity.slug}`)">
              <img src="../../assets/icons/play.svg" class="w-5 h-5" />
              <span class="pr-4">{{ $t('training.activity.replay_routine', { name: activity.name }) }}</span>
            </button>
            <button :class="getMenuItemButtonClass()" @click="$router.push('/routines')">
              <img :src="activityTypeIconUrl()" class="h-5 w-5">
              <span class="pr-4">
                {{ $t('training.activity.play_another_routine') }}
              </span>
            </button>
          </MenuItem>
          <!-- Bootcamp -->
          <MenuItem as="div" v-if="activity.list_type == 'bootcamp'">
            <button
              :class="getMenuItemButtonClass()"
              v-for="difficulty in ['beginner', 'intermediate', 'advanced']"
              @click="$router.push(`/bootcamp/${activity.slug}?difficulty=${difficulty}`)">
              <img src="../../assets/icons/play.svg" class="w-5 h-5" />
              <span class="pr-4">{{ $t('training.activity.play_bootcamp', { name: activity.name, difficulty: difficultyName(difficulty) }) }}</span>
            </button>
            <button :class="getMenuItemButtonClass()" @click="$router.push('/bootcamp')">
              <img :src="activityTypeIconUrl()" class="h-5 w-5">
              <span class="pr-4">
                {{ $t('training.activity.play_another_bootcamp') }}
              </span>
            </button>
          </MenuItem>
        </MenuItems>
      </transition>
    </Menu>
  </h2>

  <div v-for="task in tasks" class="task-row flex overflow-x-clip items-center px-4 py-3 mr-1 border-t border-gray-900 bg-gray-800 text-gray-100">
    <Task :live="live" :task="task" :hideElo="activity.list_type == 'bootcamp'" :modCategories="modCategories" :selectedMetric="selectedMetric" />
  </div>

  <div class="h-1 rounded-b-lg mb-3 bg-gray-800 mr-1" />
</template>

<script setup>
import moment from 'moment';
import { computed } from 'vue';
import {
  Menu, MenuButton, MenuItems, MenuItem,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import { useI18n } from 'vue-i18n';
import { toTitleCase } from '../../utils/formatters.js';
import Task from './Task.vue';

const { t, te } = useI18n();
const props = defineProps({
  live: {
    type: Boolean,
    required: true,
  },
  activity: {
    type: Object,
    required: true,
  },
  modCategories: {
    type: Array,
    required: true,
  },
  selectedMetric: {
    type: Object,
    required: false,
  },
});

const firstTask = computed(() => props.activity.tasks.find((task) => task.completion != null) ?? props.activity.tasks[0]);

const tasks = computed(() => {
  if (props.activity.list_type === 'bootcamp') {
    const difficulty = firstTask.value?.difficulty;
    if (difficulty) {
      const bootcampTasks = props.activity.tasks.filter((task) => task.difficulty === difficulty)
        .filter((task) => !task.is_exam || task.completion);
      // NOTE(ssaunier): Put exams at the end, as weight-based order put them first.
      return [...bootcampTasks.filter((task) => !task.is_exam), ...bootcampTasks.filter((task) => task.is_exam)];
    }
  }

  return props.activity.tasks;
});

function activityTypeIconUrl(title = false) {
  if (props.activity.coach_curated && title) {
    return new URL('../../assets/activity_type/coach.svg', import.meta.url).href;
  }
  return new URL(`../../assets/activity_type/${props.activity.list_type}.svg`, import.meta.url).href;
}

function difficultyName(difficulty) {
  const key = `training.shared.difficulty_${difficulty}`;
  return te(key) ? t(key) : toTitleCase(difficulty);
}

const activityTypeName = computed(() => {
  if (props.activity.coach_curated) {
    return t('training.activity.coach');
  }
  const key = `training.activity.type_${props.activity.list_type}`;
  return te(key) ? t(key) : toTitleCase(props.activity.list_type);
});

const startedAt = computed(() => {
  const datetime = firstTask.value?.completion?.started_at;
  if (datetime) {
    return moment(datetime).format('h:mm A');
  }
});

const difficulty = computed(() => {
  if (props.activity.list_type == 'bootcamp') {
    return `- ${difficultyName(firstTask.value?.difficulty ?? 'beginner')}`;
  }
});

function getPlayButtonClass() {
  return 'text-xs h-8 px-3 mr-1 text-text-primary font-medium flex cursor-pointer items-center justify-center gap-1 rounded-md bg-primary-600 hover:bg-primary-500 duration-200';
}

function getMenuItemButtonClass() {
  return 'flex items-center gap-2 w-full p-3 text-sm text-text-primary hover:bg-gray-700 whitespace-nowrap';
}
</script>

<style scoped>
.activity-type-label {
  font-size: 90%;
}
.activity-type-label img {
  display: inline;
  vertical-align: -3px;
  width: 16px;
  height: 16px;
  filter: contrast(150%) saturate(100%);
}
</style>
